import {
  GridPreProcessEditCellProps,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { isDefAndNotNull } from '@utils/def';
import moment from 'moment';

import { GridTableStatusTypes } from './enums';
import { TableColumns } from './types';

export const addStatusGridColumn = <T extends object>(
  rows: T[],
): (T & { gridTableStatus: GridTableStatusTypes | undefined })[] =>
  rows.map((row) => ({
    ...row,
    gridTableStatus:
      'gridTableStatus' in row
        ? (row.gridTableStatus as GridTableStatusTypes)
        : undefined,
  }));

export const areObjectsEqual = <T extends Record<string, any>>(
  obj1: T,
  obj2: T,
): boolean => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => obj1[key] === obj2[key]);
};

export const validationForEmptyEditCell = (
  params: GridPreProcessEditCellProps,
) => {
  const hasError =
    !isDefAndNotNull(params.props.value) || params.props.value === '';
  return { ...params.props, error: hasError };
};

export const getRowsListById = <T extends GridValidRowModel>(
  rows: T[],
  columnName: keyof T,
  selectedRows: GridRowSelectionModel,
): T[] => rows.filter((row) => selectedRows.includes(row[columnName]));

export const formatDateStringToDate = (date: string): string | null =>
  isDefAndNotNull(date) ? date : null;

export const formatterDate = (date: string): string =>
  isDefAndNotNull(date) ? moment(date).format('YYYY-MM-DD') : '';

export const formatterDateParser = (date: string): string => {
  const isValidDate = moment(date, 'YYYY-MM-DD').isValid();

  return isDefAndNotNull(date) && isValidDate
    ? moment(date).format('YYYY-MM-DD')
    : '';
};

export const formatterPrice = (cell: number | null, removeTenths?: boolean) => {
  if (!isDefAndNotNull(cell)) return '';

  const value = parseFloat(cell.toString())
    .toLocaleString('en-us', { minimumFractionDigits: 2 })
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return removeTenths ? value.replace(/\.\d+$/, '') : value;
};

const parseDateToYYYYMMDD = (dateString: string): string | null => {
  if (!dateString?.trim()) return null;

  const trimmedDate = dateString.trim();
  const formats = [
    'YYYY-MM-DD',
    'D-MMM-YYYY', // 22-Mar-2022
    'M/D/YYYY', // 3/22/2022
    'MM/DD/YYYY', // 03/22/2022
    'DD-MMM-YYYY',
  ];
  const parsedDate = moment(trimmedDate, formats, true);

  if (parsedDate.isValid()) {
    return parsedDate.format('YYYY-MM-DD');
  }

  const excelSerial = parseFloat(trimmedDate);
  if (excelSerial > 59 && excelSerial < 2958466) {
    return moment('1899-12-30').add(excelSerial, 'days').format('YYYY-MM-DD');
  }

  return null;
};

export const convertPastedCell = (
  cellValue: string | null | undefined,
  columnType: TableColumns['type'],
) => {
  if (!isDefAndNotNull(cellValue) || cellValue === '') {
    return null;
  }
  switch (columnType) {
    case 'number': {
      const cleanedValue = parseFloat(cellValue.replace(/,/g, ''));
      return isNaN(cleanedValue) ? cellValue : cleanedValue;
    }
    case 'boolean': {
      return Boolean(cellValue);
    }
    case 'date': {
      return parseDateToYYYYMMDD(cellValue);
    }
    default: {
      return cellValue;
    }
  }
};
